import { entries, every, get, isEmpty } from 'lodash';
import { KEYPHRASE_GROUP_NAME_FOR_START, KEYPHRASE_GROUP_NAME_FOR_STOP, SAMPLE_RATE_HERTZ } from '../../constants/backend';
import { isKeyphraseIntegrationMode, pathSpottingConfigMode } from '../../utils/background/asr';
import { getDeviceSampleRate } from '../../utils/background/Utils';
import { calculateScore } from '../../utils/points';
import type { AsrConfigErrors, AsrRecognitionConfig, AsrSpottingConfig, AsrVendorConfig, RootState } from '../types';
import { enableAnonymization, getAppConfig, getCallMetadataOnlyFlag, getEnableAsrDebugFlag, shouldSendSystemSoundToAsr } from './config';
import { getCallDetectionSessionId, getCallId, getLastCallId, getSessionDurationMsec, isCallStarted } from './call';
import { getSafeSystemInfo, getAuthToken } from './auth';

const EMPTY_SPOTTING_CONFIG: AsrSpottingConfig = { whitelist: [], blacklist: [] };
const EMPTY_ASR_CONFIG_ERRORS: AsrConfigErrors = {};

interface KeyphraseItem {
    groupName?: string;
    head?: string[];
    text?: string;
}

interface ScoreWord {
    count: number;
    alternatives: { count: number; points: number }[];
}

const toScoreWords = (whitelist: RootState['trainer']['whitelist']): ScoreWord[] =>
    whitelist
        .map(item => ({
            count: Number(item.count),
            alternatives: (item.alternatives || [])
                .map(alt => ({
                    count: Number(alt.count),
                    points: Number(alt.points)
                }))
                .filter(alt => Number.isFinite(alt.count) && Number.isFinite(alt.points))
        }))
        .filter(item => Number.isFinite(item.count));

export const getAsrConfig = (state: RootState) => {
    const asrConfig = state.backend.asr.config;
    const appConfig = getAppConfig(state);
    const spottingConfig = asrConfig.spottingConfig || EMPTY_SPOTTING_CONFIG;

    return {
        ...asrConfig,
        spottingConfig: pathSpottingConfigMode(spottingConfig, isKeyphraseIntegrationMode(appConfig || {}))
    };
};

const getResultScore = (state: RootState, whitelist: RootState['trainer']['whitelist']) => {
    const score = calculateScore(toScoreWords(whitelist));
    if (isEmpty(whitelist)) {
        return score;
    }
    const bonusScore = every(whitelist, 'count') ? state.backend.config.bonusScore : 0;
    return score + bonusScore;
};

export function getLatestAsrResults(state: RootState) {
    const { micVoiceMetrics, micResults, timelineTranscripts, config } = state.backend.asr;

    const keyPhraseIntegrationWords = {
        start: [] as string[],
        stop: [] as string[]
    };

    const appConfig = getAppConfig(state);

    if (appConfig && isKeyphraseIntegrationMode(appConfig)) {
        const { whitelist } = getAsrConfig(state).spottingConfig;
        whitelist.forEach((item: KeyphraseItem) => {
            if ([KEYPHRASE_GROUP_NAME_FOR_START].includes(item.groupName || '')) {
                keyPhraseIntegrationWords.start.push(...(item.head || []));
                return false;
            }
            if ([KEYPHRASE_GROUP_NAME_FOR_STOP].includes(item.groupName || '')) {
                keyPhraseIntegrationWords.stop.push(...(item.head || []));
                return false;
            }
        });
    }

    const filteredWhitelist = micResults.whitelist.filter((item: KeyphraseItem) => {
        return ![KEYPHRASE_GROUP_NAME_FOR_START, KEYPHRASE_GROUP_NAME_FOR_STOP].includes(item.text || '');
    });

    const duration = getSessionDurationMsec(state);
    const currentCallId = getCallId(state);
    const lastCallId = getLastCallId(state);
    const started = isCallStarted(state);

    return {
        ...micResults,
        whitelist: filteredWhitelist,
        ...micVoiceMetrics,
        language: config.languageCode,
        callDuration: Math.floor(duration / 1000),
        isStarted: started,
        timelineTranscripts,
        resultScore: getResultScore(state, filteredWhitelist),
        daemonVersion: get(state.backend, 'main.version'),
        callId: started ? currentCallId : lastCallId,
        keyPhraseIntegrationWords
    };
}

export function getEnabledASRVendor({ vendors = {} }: AsrRecognitionConfig = {}) {
    const asrEntries = entries(vendors);
    const enabledEntries = asrEntries.filter(([, config]: [string, AsrVendorConfig]) => !!config.enabled);

    if (enabledEntries.length !== 1) {
        throw new Error('ASR Vendor Configuration Error - one ASR Vendor must be enabled');
    }

    return enabledEntries[0];
}

export function getAsrErrorState(state: RootState) {
    return { asrConfigErrors: state.backend.asr.asrConfigErrors || EMPTY_ASR_CONFIG_ERRORS };
}

export function buildRecognitionConfig(state: RootState) {
    return {
        shouldSendSystemSound: shouldSendSystemSoundToAsr(state),
        enableAnonymization: enableAnonymization(state),
        sessionId: getCallDetectionSessionId(state),
        sampleRateHertz: getDeviceSampleRate(state) || SAMPLE_RATE_HERTZ,
        systemInfo: getSafeSystemInfo(state),
        jwt: getAuthToken(state),
        callId: getCallId(state),
        ...getAsrConfig(state),
        enableCallMetadataOnly: getCallMetadataOnlyFlag(state),
        enableAsrDebug: getEnableAsrDebugFlag(state)
    };
}
